/**
 * 소셜 로그인 provider — **단일 출처**.
 * 서버(어댑터·검증)와 웹(로그인 버튼·프로필 표기)이 같은 목록과 라벨을 쓴다.
 * provider를 추가/제거하면 양쪽이 함께 따라온다.
 * 개인정보 미수집 원칙: 여기서 다루는 건 표기용 라벨뿐, 사용자 정보는 없다.
 */
// 구글 로그인은 2026-08-19 제거 (네이버·카카오만 운영).
// 되살리려면 여기에 'google'을 넣고 providers.ts의 어댑터를 복원하면 된다.
export const AUTH_PROVIDERS = ['naver', 'kakao'] as const;
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

export const AUTH_PROVIDER_LABELS: Record<
  AuthProvider,
  {
    /** 짧은 이름 — "네이버 로그인" 같은 표기용 */
    ko: string;
    /**
     * 로그인 버튼 문구.
     * ⚠️ 각 사의 디자인 가이드가 문구를 지정한다. 임의로 바꾸면 네이버 검수에서 반려된다.
     *  - 네이버: "네이버 아이디로 로그인"
     *  - 카카오: "카카오 로그인" (축약 "로그인"까지만 허용)
     */
    button: string;
    /** 버튼 배경 — 각 사의 브랜드 가이드가 지정한 고정 색 (테마 토큰 대상 아님) */
    bg: string;
    /** 버튼 글자색 */
    fg: string;
  }
> = {
  naver: { ko: '네이버', button: '네이버 아이디로 로그인', bg: '#03C75A', fg: '#ffffff' },
  kakao: { ko: '카카오', button: '카카오 로그인', bg: '#FEE500', fg: '#191919' },
};

export const isAuthProvider = (v: string): v is AuthProvider =>
  (AUTH_PROVIDERS as readonly string[]).includes(v);

/**
 * 계정에 저장될 수 있는 provider 전체.
 *
 * `AUTH_PROVIDERS`(웹 로그인 버튼)와 **일부러 분리**한다. 토스 로그인은
 * 미니앱 안에서만 가능한데, 여기에 합치면 웹 로그인 화면에 토스 버튼이 뜬다.
 */
export const ACCOUNT_PROVIDERS = [...AUTH_PROVIDERS, 'toss'] as const;
export type AccountProvider = (typeof ACCOUNT_PROVIDERS)[number];

/** 표기용 이름 — "○○ 로그인" 자리 (내 정보 화면) */
export const ACCOUNT_PROVIDER_LABELS: Record<AccountProvider, string> = {
  naver: AUTH_PROVIDER_LABELS.naver.ko,
  kakao: AUTH_PROVIDER_LABELS.kakao.ko,
  toss: '토스',
};

/**
 * ── 전송 계층 ─────────────────────────────────────────────────────
 *
 * 웹(tarotwith.com)은 세션·기기 식별을 **쿠키**로 한다. 그런데 앱인토스 미니앱은
 * 토스가 호스팅하는 오리진(`*.tossmini.com`)에서 돌아 우리 쿠키가 전부 **서드파티**가 되고,
 * iOS 13.4+ 는 이를 차단한다. 그 표면에서는 같은 값을 **헤더로** 실어 보낸다.
 *
 * 서버는 쿠키를 먼저 보고, 없을 때만 헤더를 본다 — 웹 동작은 그대로 두기 위해서다.
 * doc/apps-in-toss-setup.md STEP 3
 */

/** 기기 식별값 헤더 — 하루 이용 횟수를 세는 주체 (무작위 ID, 개인정보 아님) */
export const DEVICE_ID_HEADER = 'X-Device-Id';

/** 세션 토큰 헤더 값 접두사 — `Authorization: Bearer <jwt>` */
export const BEARER_PREFIX = 'Bearer ';

/**
 * 기기 식별값 형식. 서버가 헤더를 받아들일지 판단하는 기준이라 양쪽이 같은 규칙을 봐야 한다.
 * (쿠키든 헤더든 클라이언트가 정하는 값이라는 점은 같다 — 완벽한 식별이 목적이 아니다.
 *  실제 비용 방어선은 전역 일일 상한이다. doc/usage-limit.md)
 */
export const DEVICE_ID_PATTERN = /^[A-Za-z0-9]{8,64}$/;
export const isDeviceId = (v: string): boolean => DEVICE_ID_PATTERN.test(v);
